import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { testApi } from "@/api/testApi";
import { Button } from "@/components/ui/button";
import { CertificateContent } from "@/utils/certification";
import {
  certificateDownloadHint,
  downloadCertificatePdf,
} from "@/utils/certificateDownload";
import LoadingOverlay from "@/components/ui/LoadingOverlay";

export default function CertificateView() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  // `?print=1` is kept for old links; it now triggers a PDF download.
  const autoDownload = searchParams.get("print") === "1";
  const [loading, setLoading] = useState(true);
  const [certificationData, setCertificationData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const didAutoDownload = useRef(false);

  useEffect(() => {
    const fetchCertification = async () => {
      if (!courseId) {
        setErrorMessage("Course not found.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");
        const response = await testApi.certification(courseId);

        if (response.success && response.data?.eligible) {
          setCertificationData(response.data);
        } else {
          setCertificationData(null);
          setErrorMessage(
            response.data?.message ||
              "You must pass the assessment with the required score to download your certificate.",
          );
        }
      } catch (error) {
        console.error("Failed to load certificate:", error);
        setCertificationData(null);
        setErrorMessage("Failed to load certificate. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCertification();
  }, [courseId]);

  const handleDownload = async () => {
    if (!certificationData || downloading) return;
    setDownloading(true);
    setDownloadError("");
    try {
      await downloadCertificatePdf(courseId);
    } catch (err) {
      console.error("Certificate download failed:", err);
      setDownloadError("Couldn't download the certificate. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (
      !autoDownload ||
      loading ||
      !certificationData ||
      didAutoDownload.current
    ) {
      return;
    }
    didAutoDownload.current = true;
    handleDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload, loading, certificationData]);

  if (loading) {
    return <LoadingOverlay isLoading />;
  }

  if (!certificationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6fafc] p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-md border border-gray-200 p-8 text-center">
          <h1 className="text-xl font-semibold text-[#2B6A7F] mb-3">
            Certificate Unavailable
          </h1>
          <p className="text-gray-700">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="no-print fixed inset-x-0 top-0 z-50 flex flex-col sm:flex-row items-center justify-between gap-3 border-b border-gray-200 bg-[#f6fafc] px-4 py-3 shadow-sm">
        <p
          className={`text-sm text-center sm:text-left ${downloadError ? "text-red-600" : "text-gray-600"}`}
        >
          {downloadError || certificateDownloadHint()}
        </p>
        <Button
          type="button"
          className="shrink-0 bg-[#5D93A5] hover:bg-[#4a7a8a] text-white rounded-full px-6"
          disabled={downloading}
          onClick={handleDownload}
        >
          {downloading ? "Preparing…" : "Download PDF"}
        </Button>
      </div>

      <div className="pt-24 sm:pt-16">
        <CertificateContent data={certificationData} />
      </div>
    </div>
  );
}
